/* eslint-disable react/prop-types */
"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PremiumCodeLabel from "../common/PremiumCodeLabel";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";

const getParticipantAvatarSrc = (participant) =>
  resolveUserAvatarSrc(participant, "/assets/events/default_avatar.png");

export const prioritizePremiumParticipants = (participants = []) => {
  const premium = [];
  const standard = [];

  participants.forEach((participant) => {
    if (participant?.has_premium_subscription) {
      premium.push(participant);
    } else {
      standard.push(participant);
    }
  });

  return [...premium, ...standard];
};

/**
 * ParticipantsSection
 * Reusable participants scroller used in EventDetailsFullscreen (desktop & mobile variants).
 *
 * Props:
 * - variant: 'desktop' | 'mobile'
 * - participants: array of { code, avatar, avatar_custom_url, attended, intent_confirmed, intent_status, slot_confirm_status, host_ticket_paid }
 * - onOpenParticipant: (participant) => void
 * - loadingCode: string | null
 * - userStorageCode: string | null
 * - intentStatus: 'unknown' | 'confirmed'
 * - activationDateBadgesEnabled: boolean
 * - showPaymentBadges: boolean
 * - isHost: boolean — only hosts see intent badges and payment badges
 * - reserveUI: { lockActive, lockedRemaining, userOldOrBlocked }
 * - isFull: boolean
 * - unlockBeforeLabel?: string | null
 */
export default function ParticipantsSection({
  variant = "desktop",
  participants = [],
  participantsCount = null,
  onOpenParticipant,
  loadingCode,
  userStorageCode,
  intentStatus,
  activationDateBadgesEnabled = false,
  showPaymentBadges = false,
  isHost = false,
  reserveUI = { lockActive: false, lockedRemaining: 0, userOldOrBlocked: false },
  isFull = false,
  unlockBeforeLabel = null,
}) {
  const { t } = useTranslation("events");

  const maxParticipants =
    "max_participants" in reserveUI && reserveUI.max_participants
      ? reserveUI.max_participants
      : "∞";
  const totalParticipants =
    participantsCount != null ? Number(participantsCount) || 0 : participants.length;
  const isDesktop = variant === "desktop";
  const hasLockedReservedSeats =
    reserveUI.lockActive &&
    reserveUI.lockedRemaining > 0 &&
    !isFull;
  const hasParticipantVisuals = participants.length > 0 || hasLockedReservedSeats;
  const orderedParticipants = useMemo(
    () => prioritizePremiumParticipants(participants),
    [participants]
  );

  const listRef = useRef(null);
  const [showArrow, setShowArrow] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (!listRef.current) return;
      setShowArrow(listRef.current.scrollWidth > listRef.current.clientWidth);
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [participants, hasLockedReservedSeats, reserveUI.lockedRemaining]);

  const Title = (
    <h2 className={`${isDesktop ? "text-xl" : "text-2xl"} font-bold text-white mb-4`}>
      {t("participants_section.title")}: ({totalParticipants}/{maxParticipants})
    </h2>
  );

  return (
    <div className={`${isDesktop ? (hasParticipantVisuals ? "text-center mb-6" : "text-center mb-4") : "flex flex-col gap-2.5"}`}>
      {isDesktop ? (
        Title
      ) : (
        <h2 className="text-2xl font-extrabold leading-6 text-white">
          {t("participants_section.title")}: ({totalParticipants}/{maxParticipants})
        </h2>
      )}

      {hasParticipantVisuals && (
        <div
          className={`relative ${
            isDesktop ? "mt-2 mb-2 flex w-full min-w-0 justify-center" : "mt-2"
          }`}
        >
          <div
            ref={listRef}
            className={`${
              isDesktop
                ? "w-full max-w-full min-w-0 overflow-x-auto overflow-y-visible scrollbar-hide px-1 py-0.5"
                : "overflow-x-auto overflow-y-visible scrollbar-hide px-1 py-0.5"
            }`}
          >
            <div
              className={`flex w-max min-w-full flex-nowrap ${
                isDesktop ? "justify-center" : "justify-start"
              } gap-2 ${isDesktop ? "max-h-40" : ""}`}
            >
              {orderedParticipants.map((p, idx) => {
                const activationDateConfirmed = (
                  activationDateBadgesEnabled &&
                  p.activation_date_status === "confirmed"
                );
                const showIcon = p.attended
                  ? "attended"
                  : (
                      (
                        p.intent_confirmed ||
                        p.intent_status === "confirmed" ||
                        p.slot_confirm_status === "confirmed" ||
                        (p.code === userStorageCode && intentStatus === "confirmed") ||
                        activationDateConfirmed
                      )
                        ? "intent"
                        : null
                    );
                return (
                  <button
                    key={p.code || idx}
                    onClick={(e) => { e.stopPropagation(); onOpenParticipant?.(p); }}
                    disabled={loadingCode != null || !p.code || p.is_deleted_account}
                    className={`flex-shrink-0 flex flex-col items-center ${isDesktop ? "" : "gap-1"}`}
                  >
                      <div className="relative">
                        <img
                          src={getParticipantAvatarSrc(p)}
                          alt={t("participants_section.avatar_alt", { code: p.code })}
                          className={`rounded-full w-[55px] h-[55px] object-cover ${
                            p.avatar_custom_url || p.avatar_url ? "ring-2 ring-white/80" : ""
                          }`}
                        />
                        {showIcon === "attended" && (
                          <img
                            src="/assets/events/attended_icon.svg"
                            alt={t("participants_section.attended_icon_alt")}
                            className="absolute bottom-0 right-0 w-5 h-5"
                          />
                        )}
                        {isHost && showIcon === "intent" && (
                          <img
                            src="/assets/events/intend_icon.svg"
                            alt={t("participants_section.intent_icon_alt")}
                            className="absolute bottom-0 right-0 w-5 h-5"
                          />
                        )}
                        {isHost && showPaymentBadges && p.host_ticket_paid && (
                          <div
                            className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full border border-white bg-green-500 flex items-center justify-center shadow-[0_0_10px_rgba(34,197,94,0.55)]"
                            title={t("participants_section.paid_icon_alt")}
                            aria-label={t("participants_section.paid_icon_alt")}
                          >
                            <span className="text-[11px] leading-none font-extrabold text-white">
                              €
                            </span>
                          </div>
                        )}
                      </div>
                    <PremiumCodeLabel
                      code={p.code}
                      isPremium={Boolean(p?.has_premium_subscription)}
                      className={`${
                        isDesktop ? "mt-1.5 text-sm" : "text-lg"
                      } text-white font-extrabold`}
                    />
                  </button>
                );
              })}

              {hasLockedReservedSeats && (
                Array.from({
                  length: Math.min(
                    reserveUI.lockedRemaining,
                    maxParticipants === "∞" ? reserveUI.lockedRemaining : maxParticipants
                  )
                }).map((_, i) => (
                  <div key={`lock-${variant}-${i}`} className={`flex-shrink-0 flex flex-col items-center ${isDesktop ? "" : "gap-1"}`}>
                    <div className="relative">
                      <div className="rounded-full w-[55px] h-[55px] bg-white/30" />
                      <img
                        src="/assets/events/lock_icon.svg"
                        alt={t("participants_section.lock_reserved_alt")}
                        className="absolute bottom-0 right-0 w-5 h-5"
                      />
                    </div>
                    <span
                      className={`${isDesktop ? "text-sm" : "text-lg"} text-white/80 font-extrabold select-none`}
                    >
                      {t("participants_section.lock_reserved_label")}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {showArrow && (
            <img
              src="/assets/vault/arrow_down.svg"
              alt={t("participants_section.scroll_right_alt")}
              className={`pointer-events-none absolute right-0 ${
                isDesktop ? "top-[30%] -translate-y-1/2 h-8 w-8" : "top-[35%] -translate-y-1/2 h-8 w-8"
              } rounded-xl px-1 rotate-90`}
            />
          )}
        </div>
      )}

      {hasLockedReservedSeats && (
        <p className={`${isDesktop ? "mt-1 text-center text-white/70 text-xs flex items-center justify-center gap-1" : "mt-1 text-center text-white/70 text-xs flex flex-wrap items-center justify-center gap-1 leading-tight"}`}>
          {isDesktop ? (
            <>
              <img
                src="/assets/events/lock_icon.svg"
                alt={t("participants_section.lock_reserved_alt")}
                className="w-5 h-5"
              />
              <span className="font-semibold">{Math.min(reserveUI.lockedRemaining, maxParticipants === "∞" ? Infinity : maxParticipants)}</span>
              <span>
                {" "}
                {t("participants_section.reserved_seats")}
                {unlockBeforeLabel && (
                  <>
                    {" "}
                    •{" "}
                    {t("participants_section.unlocks_label", {
                      label: unlockBeforeLabel,
                    })}
                  </>
                )}
              </span>
            </>
          ) : (
            <>
              <span className="inline-flex items-center whitespace-nowrap">
                <img src="/assets/events/lock_icon.svg" alt="" aria-hidden="true" className="w-4 h-4" />
                <span className="ml-1 font-semibold">{Math.min(reserveUI.lockedRemaining, maxParticipants === "∞" ? Infinity : maxParticipants)}</span>
              </span>
              <span>{t("participants_section.reserved_seats")}</span>
              {unlockBeforeLabel && (
                <>
                  <span className="select-none">•</span>
                  <span>
                    {t("participants_section.unlocks_label", {
                      label: unlockBeforeLabel,
                    })}
                  </span>
                </>
              )}
            </>
          )}
        </p>
      )}
    </div>
  );
}
